import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { TelegramWebApp, TelegramUser } from '../lib/types';
import { APIService } from '../lib/apiService';

interface TelegramWebAppContextType {
  webApp: TelegramWebApp | null;
  user: TelegramUser | null;
  isReady: boolean;
  isTelegram: boolean;
  initData: string | null;
  authenticateUser: () => Promise<boolean>;
  isAuthenticated: boolean;
  authError: string | null;
  isFullscreen: boolean;
  requestFullscreen: () => void;
  exitFullscreen: () => void;
  // Enhanced state management integration
  configureBackButton: (isVisible: boolean, onClick?: () => void) => void;
  configureMainButton: (config: {
    text?: string;
    isVisible?: boolean;
    isEnabled?: boolean;
    onClick?: () => void;
  }) => void;
  showHapticFeedback: (type: 'impact' | 'notification' | 'selection', style?: string) => void;
  setHeaderColor: (color: string) => void;
}

const TelegramWebAppContext = createContext<TelegramWebAppContextType | null>(null);

export const useTelegramWebApp = () => {
  const context = useContext(TelegramWebAppContext);
  if (!context) {
    throw new Error('useTelegramWebApp must be used within TelegramWebAppProvider');
  }
  return context;
};

interface TelegramWebAppProviderProps {
  children: ReactNode;
}

/**
 * Secure Telegram WebApp provider with HMAC-SHA256 authentication
 * Replaces unsafe initDataUnsafe usage with proper validation
 */
export const TelegramWebAppProvider: React.FC<TelegramWebAppProviderProps> = ({ children }) => {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [user, setUser] = useState<TelegramUser | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isTelegram, setIsTelegram] = useState(false);
  const [initData, setInitData] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    // Check if we're running in Telegram WebApp environment
    const checkTelegramEnvironment = () => {
      if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
        const tgWebApp = window.Telegram.WebApp as TelegramWebApp;
        
        console.log('   Version:', tgWebApp.version);
        console.log('   Platform:', tgWebApp.platform);
        console.log('   InitData length:', tgWebApp.initData?.length || 0);

        setWebApp(tgWebApp);
        setIsTelegram(true);

        // Use secure initData instead of initDataUnsafe
        if (tgWebApp.initData) {
          setInitData(tgWebApp.initData);
        } else {
          console.warn('⚠️ No initData available - running in development mode?');
        }

        // ✅ CRITICAL FIX: Signal that the WebApp is ready ONLY ONCE with singleton guard
        // Prevents multiple initialization calls that cause permission loops
        if (!window.__telegramReadyCalled) {
          console.log('🚀 Calling Telegram.WebApp.ready() for the FIRST time');
          tgWebApp.ready();
          window.__telegramReadyCalled = true;
          setIsReady(true);
        } else {
          console.log('♻️ Telegram.WebApp.ready() already called, skipping duplicate call');
          setIsReady(true);
        }

        // Configure WebApp settings
        tgWebApp.expand();
        tgWebApp.isClosingConfirmationEnabled = false;
        
        // Request full-screen mode
        if (tgWebApp.requestFullscreen) {
          console.log('🔄 Requesting full-screen mode...');
          try {
            tgWebApp.requestFullscreen();
            
            // Set header color for better contrast in full-screen
            if (tgWebApp.setHeaderColor) {
              const isDark = tgWebApp.colorScheme === 'dark';
              const headerColor = isDark ? 'hsl(var(--card))' : 'hsl(var(--background))';
              tgWebApp.setHeaderColor(headerColor);
            }
            
            // Listen for full-screen events
            if (tgWebApp.onEvent) {
              tgWebApp.onEvent('fullscreenChanged', () => {
                console.log('📱 Full-screen state changed:', tgWebApp.isFullscreen);
                setIsFullscreen(tgWebApp.isFullscreen || false);
              });
              
              tgWebApp.onEvent('fullscreenFailed', () => {
                console.warn('⚠️ Full-screen request failed');
                setIsFullscreen(false);
              });
            }
            
          } catch (error) {
            console.warn('⚠️ Full-screen not supported or failed:', error);
          }
        } else {
          console.log('ℹ️ Full-screen API not available in this Telegram version');
        }

        return true;
      }
      return false;
    };

    // Try to detect Telegram WebApp
    if (!checkTelegramEnvironment()) {
      
      // Development mode with mock data
      const mockWebApp: Partial<TelegramWebApp> = {
        initData: '', // Empty in development
        version: '6.0',
        platform: 'web',
        colorScheme: 'light',
        themeParams: {
          bg_color: 'hsl(var(--background))',
          text_color: 'hsl(var(--foreground))',
          button_color: 'hsl(var(--service-telegram))',
          button_text_color: 'hsl(var(--primary-foreground))'
        },
        isExpanded: true,
        isFullscreen: false,
        viewportHeight: 600,
        viewportStableHeight: 600,
        MainButton: {
          text: 'Continue',
          color: 'hsl(var(--service-telegram))',
          textColor: 'hsl(var(--primary-foreground))',
          isVisible: false,
          isActive: true,
          isProgressVisible: false,
          setText: (text: string) => console.log('MainButton setText:', text),
          onClick: (_callback: () => void) => console.log('MainButton onClick'),
          show: () => console.log('MainButton show'),
          hide: () => console.log('MainButton hide'),
          enable: () => console.log('MainButton enable'),
          disable: () => console.log('MainButton disable'),
          showProgress: () => console.log('MainButton showProgress'),
          hideProgress: () => console.log('MainButton hideProgress')
        },
        BackButton: {
          isVisible: false,
          onClick: (_callback: () => void) => console.log('BackButton onClick'),
          show: () => console.log('BackButton show'),
          hide: () => console.log('BackButton hide')
        },
        HapticFeedback: {
          impactOccurred: (style) => console.log('HapticFeedback impact:', style),
          notificationOccurred: (type) => console.log('HapticFeedback notification:', type),
          selectionChanged: () => console.log('HapticFeedback selection changed')
        },
        expand: () => console.log('WebApp expand'),
        close: () => console.log('WebApp close'),
        ready: () => console.log('WebApp ready'),
        sendData: (data: string) => console.log('WebApp sendData:', data),
        requestFullscreen: () => {
          console.log('🔄 Mock: Requesting full-screen mode');
          (mockWebApp as any).isFullscreen = true;
        },
        exitFullscreen: () => {
          console.log('🔄 Mock: Exiting full-screen mode');
          (mockWebApp as any).isFullscreen = false;
        },
        setHeaderColor: (color: string) => console.log('Mock: setHeaderColor:', color),
        onEvent: (eventType: string, _callback: () => void) => console.log('Mock: onEvent:', eventType),
        offEvent: (eventType: string, _callback: () => void) => console.log('Mock: offEvent:', eventType)
      };

      const mockUser: TelegramUser = {
        id: 12345678,
        first_name: 'Dev',
        last_name: 'User',
        username: 'devuser',
        language_code: 'en',
        is_premium: false
      };

      setWebApp(mockWebApp as TelegramWebApp);
      setUser(mockUser);
      setIsTelegram(false);

      // ✅ FIX: Also set ready flag for mock development mode
      if (!window.__telegramReadyCalled) {
        console.log('🚀 Mock Telegram.WebApp.ready() called for development');
        window.__telegramReadyCalled = true;
        setIsReady(true);
      } else {
        console.log('♻️ Mock Telegram.WebApp.ready() already called, skipping');
        setIsReady(true);
      }
      
    }
  }, []);

  /**
   * Authenticate user with secure HMAC-SHA256 validation
   */
  const authenticateUser = async (): Promise<boolean> => {
    try {
      setAuthError(null);
      
      if (!initData && isTelegram) {
        throw new Error('No Telegram init data available');
      }

      if (isTelegram && initData) {
        // Production: Use secure HMAC-SHA256 validation
        
        const authResult = await APIService.authenticateWithTelegram(initData);
        
        if (authResult.accessToken) {
          setIsAuthenticated(true);
          
          // Get user profile from backend
          try {
            const profile = await APIService.getEmployeeProfile();
            const telegramUser: TelegramUser = {
              id: profile.telegram_id || 0,
              first_name: profile.first_name || profile.full_name.split(' ')[0],
              last_name: profile.last_name || profile.full_name.split(' ').slice(1).join(' '),
              username: profile.username
            };
            setUser(telegramUser);
          } catch (profileError) {
            console.warn('Could not fetch profile:', profileError);
          }
          
          return true;
        }
      } else {
        // Development: Skip authentication or use mock
        setIsAuthenticated(true);
        return true;
      }

      return false;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      console.error('❌ Telegram authentication failed:', errorMessage);
      setAuthError(errorMessage);
      setIsAuthenticated(false);
      return false;
    }
  };

  // Auto-authenticate when initData is available
  // ✅ FIXED: Added initialization guard to prevent authentication spam
  const authenticationAttempted = useRef(false);

  useEffect(() => {
    if (isReady && !isAuthenticated && !authenticationAttempted.current) {
      authenticationAttempted.current = true;
      authenticateUser();
    }
  }, [isReady]); // ✅ FIXED: Removed initData from dependencies to prevent re-authentication

  // Helper functions for full-screen management
  const requestFullscreen = useCallback(() => {
    if (webApp?.requestFullscreen) {
      console.log('🔄 Requesting full-screen mode...');
      webApp.requestFullscreen();
    } else {
      console.warn('⚠️ Full-screen API not available');
    }
  }, [webApp]);

  const exitFullscreen = useCallback(() => {
    if (webApp?.exitFullscreen) {
      console.log('🔄 Exiting full-screen mode...');
      webApp.exitFullscreen();
    } else {
      console.warn('⚠️ Full-screen API not available');
    }
  }, [webApp]);

  // Enhanced Telegram UI integration functions
  const backButtonCallback = useRef<(() => void) | null>(null);
  const configureBackButton = useCallback((isVisible: boolean, onClick?: () => void) => {
    if (!webApp?.BackButton?.offClick) {
      // Silently skip - not available in this environment
      return;
    }

    // Check Telegram WebApp version - BackButton requires version >= 6.1
    const version = webApp.version || '6.0';
    const versionNumber = parseFloat(version);

    if (!(versionNumber >= 6.1)) {
      // Silently skip if version doesn't support BackButton
      // Only log once in development mode for debugging
      if (process.env.NODE_ENV === 'development' && !window.localStorage.getItem('backbutton-version-warned')) {
        console.info(`ℹ️ Telegram BackButton requires version 6.1+, current: ${version} - skipping BackButton functionality`);
        window.localStorage.setItem('backbutton-version-warned', 'true');
      }
      return;
    }

    try {
      // Telegram adds listeners; registering a new callback does not replace the old one.
      if (backButtonCallback.current) {
        webApp.BackButton.offClick?.(backButtonCallback.current);
        backButtonCallback.current = null;
      }
      if (isVisible) {
        webApp.BackButton.show();
        if (onClick) {
          webApp.BackButton.onClick(onClick);
          backButtonCallback.current = onClick;
        }
        console.log('✅ Telegram BackButton configured:', { isVisible, hasClickHandler: !!onClick });
      } else {
        webApp.BackButton.hide();
        console.log('✅ Telegram BackButton hidden');
      }
    } catch (error) {
      console.warn('Failed to configure BackButton:', error);
    }
  }, [webApp]);

  useEffect(() => () => configureBackButton(false), [configureBackButton]);

  const configureMainButton = useCallback((config: {
    text?: string;
    isVisible?: boolean;
    isEnabled?: boolean;
    onClick?: () => void;
  }) => {
    if (!webApp?.MainButton) {
      console.warn('⚠️ Telegram MainButton not available');
      return;
    }

    try {
      const { text, isVisible, isEnabled, onClick } = config;

      if (text) {
        webApp.MainButton.setText(text);
      }

      if (isEnabled !== undefined) {
        if (isEnabled) {
          webApp.MainButton.enable();
        } else {
          webApp.MainButton.disable();
        }
      }

      if (onClick) {
        webApp.MainButton.onClick(onClick);
      }

      if (isVisible !== undefined) {
        if (isVisible) {
          webApp.MainButton.show();
        } else {
          webApp.MainButton.hide();
        }
      }

      console.log('✅ Telegram MainButton configured:', config);
    } catch (error) {
      console.warn('Failed to configure MainButton:', error);
    }
  }, [webApp]);

  const showHapticFeedback = useCallback((
    type: 'impact' | 'notification' | 'selection',
    style?: string
  ) => {
    if (!webApp?.HapticFeedback) {
      console.warn('⚠️ Telegram HapticFeedback not available');
      return;
    }

    try {
      switch (type) {
        case 'impact':
          webApp.HapticFeedback.impactOccurred(style as any || 'medium');
          break;
        case 'notification':
          webApp.HapticFeedback.notificationOccurred(style as any || 'success');
          break;
        case 'selection':
          webApp.HapticFeedback.selectionChanged();
          break;
      }
      console.log('✅ Haptic feedback triggered:', { type, style });
    } catch (error) {
      console.warn('Failed to trigger haptic feedback:', error);
    }
  }, [webApp]);

  const setHeaderColor = useCallback((color: string) => {
    if (!webApp?.setHeaderColor) {
      console.warn('⚠️ Telegram setHeaderColor not available');
      return;
    }

    try {
      webApp.setHeaderColor(color);
      console.log('✅ Header color set:', color);
    } catch (error) {
      console.warn('Failed to set header color:', error);
    }
  }, [webApp]);

  const contextValue: TelegramWebAppContextType = {
    webApp,
    user,
    isReady,
    isTelegram,
    initData,
    authenticateUser,
    isAuthenticated,
    authError,
    isFullscreen,
    requestFullscreen,
    exitFullscreen,
    configureBackButton,
    configureMainButton,
    showHapticFeedback,
    setHeaderColor,
  };

  return (
    <TelegramWebAppContext.Provider value={contextValue}>
      {children}
    </TelegramWebAppContext.Provider>
  );
};

// Global type declaration for Telegram WebApp
declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
    // ✅ NEW: Singleton guard flag for Telegram initialization
    __telegramReadyCalled?: boolean;
    // ✅ NEW: Permission state cache for persistence
    __telegramPermissionCache?: {
      camera?: 'granted' | 'denied' | 'prompt';
      microphone?: 'granted' | 'denied' | 'prompt';
      geolocation?: 'granted' | 'denied' | 'prompt';
      writeAccess?: boolean;
      contact?: boolean;
      timestamp?: number;
    };
  }
}
